/**
 * Site Authentication Store
 * Manages site-wide password protection state
 *
 * This is separate from the user auth store (useAuthStore) which handles
 * JWT-based user authentication. Site auth is a simpler password-gate
 * that protects the entire application.
 */

import { create } from 'zustand';
import {
  getSiteAuthStatus,
  siteLogin,
  siteDevBypass,
  siteLogout,
  type SiteAuthStatus,
} from '@/services/siteAuthApi';

/**
 * Site auth state interface
 */
interface SiteAuthState {
  // State
  authEnabled: boolean;
  authenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // "View as Worker" mode — admin sees the UI as a non-admin
  viewAsWorker: boolean;

  // Actions
  checkStatus: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  devBypass: () => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  toggleViewAsWorker: () => void;
}

/**
 * Site auth store
 */
export const useSiteAuthStore = create<SiteAuthState>((set, get) => ({
  authEnabled: false,
  authenticated: true, // Default to true until we check
  isAdmin: true, // Default to true until we check
  isLoading: true,
  isInitialized: false,
  error: null,
  viewAsWorker: false,

  checkStatus: async () => {
    try {
      set({ isLoading: true, error: null });
      const status: SiteAuthStatus = await getSiteAuthStatus();
      set({
        authEnabled: status.authEnabled,
        authenticated: status.authenticated,
        isAdmin: status.isAdmin ?? false,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      // If we can't check status, assume auth is not enabled
      // This allows the app to work when the backend is unreachable
      console.error('Failed to check site auth status:', error);
      set({
        authEnabled: false,
        authenticated: true,
        isLoading: false,
        isInitialized: true,
        error: 'Failed to check authentication status',
      });
    }
  },

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      const response = await siteLogin(email, password);

      if (response.success) {
        set({
          authenticated: true,
          isAdmin: response.isAdmin ?? false,
          isLoading: false,
          error: null,
        });
        return true;
      } else {
        set({
          authenticated: false,
          isLoading: false,
          error: response.error || 'Login failed',
        });
        return false;
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Invalid password';

      // Handle axios error response
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        const serverError = axiosError.response?.data?.error;
        if (serverError) {
          set({
            authenticated: false,
            isLoading: false,
            error: serverError,
          });
          return false;
        }
      }

      set({
        authenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      return false;
    }
  },

  devBypass: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await siteDevBypass();
      if (response.success) {
        set({ authenticated: true, isAdmin: response.isAdmin ?? true, isLoading: false, error: null });
        return true;
      }
      set({ isLoading: false, error: response.error || 'Bypass failed' });
      return false;
    } catch {
      set({ isLoading: false, error: 'Dev bypass failed' });
      return false;
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      await siteLogout();
      set({
        authenticated: false,
        isLoading: false,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state
      set({
        authenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  toggleViewAsWorker: () => {
    set((state) => ({ viewAsWorker: !state.viewAsWorker }));
  },
}));

/**
 * Hook for site auth
 */
export function useSiteAuth() {
  const authEnabled = useSiteAuthStore((state) => state.authEnabled);
  const authenticated = useSiteAuthStore((state) => state.authenticated);
  const realIsAdmin = useSiteAuthStore((state) => state.isAdmin);
  const viewAsWorker = useSiteAuthStore((state) => state.viewAsWorker);
  const isLoading = useSiteAuthStore((state) => state.isLoading);
  const isInitialized = useSiteAuthStore((state) => state.isInitialized);
  const error = useSiteAuthStore((state) => state.error);
  const checkStatus = useSiteAuthStore((state) => state.checkStatus);
  const login = useSiteAuthStore((state) => state.login);
  const devBypass = useSiteAuthStore((state) => state.devBypass);
  const logout = useSiteAuthStore((state) => state.logout);
  const clearError = useSiteAuthStore((state) => state.clearError);
  const toggleViewAsWorker = useSiteAuthStore((state) => state.toggleViewAsWorker);

  // When "View as Worker" is active, isAdmin returns false
  const isAdmin = realIsAdmin && !viewAsWorker;

  // Derived state: should show login
  const requiresLogin = authEnabled && !authenticated;

  return {
    authEnabled,
    authenticated,
    isAdmin,
    /** True admin status — unaffected by "View as Worker" toggle */
    isRealAdmin: realIsAdmin,
    viewAsWorker,
    isLoading,
    isInitialized,
    error,
    requiresLogin,
    checkStatus,
    login,
    devBypass,
    logout,
    clearError,
    toggleViewAsWorker,
  };
}
